// Request diagnostics — turns opaque fetch failures into actionable, self-diagnosing errors.
//
// Motivation: several prod tickets (Cyrillic legal-name WAF block; CA "Unauthorized"
// from the office network) all surfaced to the user as the same generic
// "An error occurred while processing the request", forcing a HAR capture to diagnose.
// This module classifies the failure client-side, gives the user a short reference code
// they can quote, and logs full technical detail to the console for support.
//
// See docs/investigations/waf-owasp-blocking/ and docs/investigations/misc/ for the cases.

export type DiagnosticCategory =
    | 'network-or-blocked' // fetch rejected: no HTTP response (offline, DNS, CORS/preflight, corporate proxy/firewall)
    | 'auth'               // 401/403 with a token expected — session/proxy stripped auth, or not authorized
    | 'waf-blocked'        // response looks like a WAF/security-appliance block page (403 + no CORS/HTML body)
    | 'server-error'       // 5xx from the application
    | 'app-validation'     // 4xx the application deliberately returned (business validation)
    | 'unknown';

export interface RequestDiagnostic {
    category: DiagnosticCategory;
    /** Short stable code shown to the user, e.g. "NET-CORS", "WAF-403". */
    code: string;
    /** Short correlation id the user can quote to support (also logged). */
    reference: string;
    /** HTTP status if a response was received (0 when the request never completed). */
    status: number;
    /** User-facing, non-technical, actionable message. */
    userMessage: string;
    /** One-line technical summary for logs. */
    technical: string;
}

// A compact, sortable-ish correlation id: time + random, no PII.
function makeReference(): string {
    const t = Date.now().toString(36).toUpperCase();
    // Not security-sensitive: only a support correlation id, never used as a token or secret.
    const r = Math.random().toString(36).slice(2, 6).toUpperCase(); // NOSONAR typescript:S2245
    return `FNP-${t}-${r}`;
}

function shortUrl(url: string): string {
    try {
        const u = new URL(url, window.location.origin);
        return u.pathname; // path only — avoids logging query strings / tokens
    } catch {
        return url.split('?')[0];
    }
}

/**
 * Classify a rejected fetch (the browser never received a usable HTTP response).
 * This is the "TypeError: Failed to fetch" / CORS-preflight / offline / proxy-block case.
 */
export function diagnoseFetchRejection(error: unknown, url: string): RequestDiagnostic {
    const reference = makeReference();
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const path = shortUrl(url);
    const raw = error instanceof Error ? `${error.name}: ${error.message}` : String(error);

    const userMessage = offline
        ? 'You appear to be offline. Please check your internet connection and try again.'
        : 'The request could not reach the server. This is usually caused by your network — a corporate ' +
          'proxy, firewall or security software may be blocking access. Please try a different network ' +
          '(e.g. a mobile hotspot) to confirm, and share the reference below with your IT team.';

    const diag: RequestDiagnostic = {
        category: 'network-or-blocked',
        code: offline ? 'NET-OFFLINE' : 'NET-BLOCKED',
        reference,
        status: 0,
        userMessage: `${userMessage} (Ref: ${reference})`,
        technical: `fetch rejected for ${path} — ${raw}; navigator.onLine=${!offline}`,
    };
    logDiagnostic(diag, url);
    return diag;
}

/**
 * Classify a completed-but-failed HTTP response (resp.ok === false).
 * `bodyText` is the already-read response text (may be empty).
 */
export function diagnoseErrorResponse(
    response: { status: number; headers?: Headers },
    bodyText: string,
    url: string,
    opts: { tokenSent: boolean } = { tokenSent: true },
): RequestDiagnostic {
    const reference = makeReference();
    const status = response.status;
    const path = shortUrl(url);
    const looksHtml = /^\s*<(?:!doctype|html)/i.test(bodyText);
    const hasCors = !!response.headers?.get?.('access-control-allow-origin');

    let category: DiagnosticCategory;
    let code: string;
    let userMessage: string;

    if (status === 403 && (looksHtml || !hasCors)) {
        // A WAF / security appliance typically returns 403 with an HTML block page and no CORS header.
        // Check this before the generic auth case: an HTML/no-CORS 403 is a block page, not an auth denial.
        category = 'waf-blocked';
        code = 'WAF-403';
        userMessage =
            'The request was blocked by a security filter. This can be triggered by certain characters in ' +
            'the text (e.g. some non-Latin scripts) or by your network. Try adjusting the text; if it persists, share the reference with support.';
    } else if (status === 401 || status === 403) {
        category = 'auth';
        code = `AUTH-${status}`;
        userMessage =
            'Your session could not be authenticated. Please sign out and sign in again. If this only ' +
            'happens on your office network, a proxy or firewall may be stripping the login — share the reference with your IT team.';
    } else if (status >= 500) {
        category = 'server-error';
        code = `SRV-${status}`;
        userMessage =
            'The server encountered an error processing the request. Please try again; if it persists, share the reference with support.';
    } else if (status >= 400) {
        category = 'app-validation';
        code = `APP-${status}`;
        // For genuine app validation, prefer the server's own message if present and human-readable.
        const serverMsg = extractServerMessage(bodyText);
        userMessage = serverMsg ?? 'The request could not be completed. Please review the entered values and try again.';
    } else {
        category = 'unknown';
        code = `HTTP-${status}`;
        userMessage = 'An unexpected response was received. Please try again; if it persists, share the reference with support.';
    }

    const diag: RequestDiagnostic = {
        category,
        code,
        reference,
        status,
        userMessage: category === 'app-validation' ? userMessage : `${userMessage} (Ref: ${reference})`,
        technical:
            `HTTP ${status} for ${path} — cors=${hasCors} html=${looksHtml} tokenSent=${opts.tokenSent} ` +
            `body="${bodyText.slice(0, 200).replace(/\s+/g, ' ')}"`,
    };
    logDiagnostic(diag, url);
    return diag;
}

/** Pulls a { errors: [...] } or plain-text message out of an error body, if human-readable. */
function extractServerMessage(bodyText: string): string | undefined {
    if (!bodyText) return undefined;
    try {
        const parsed = JSON.parse(bodyText);
        if (Array.isArray(parsed?.errors) && parsed.errors.length > 0 && typeof parsed.errors[0] === 'string') {
            return parsed.errors[0];
        }
        if (typeof parsed?.message === 'string') return parsed.message;
    } catch {
        // not JSON
    }
    // Plain text, but not an HTML block page.
    if (!/^\s*<(?:!doctype|html)/i.test(bodyText) && bodyText.length <= 300) {
        return bodyText.trim() || undefined;
    }
    return undefined;
}

function logDiagnostic(diag: RequestDiagnostic, url: string): void {
    // Single structured console line so support can copy it from a screenshot/HAR-less report.
    // eslint-disable-next-line no-console
    console.error(
        `[FnP diagnostics] ${diag.code} (${diag.category}) ref=${diag.reference} ` +
        `status=${diag.status} url=${shortUrl(url)} :: ${diag.technical}`,
    );
}
